import React, { FC, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';

export type ConfirmModalVariant = 'default' | 'danger';

export interface ConfirmModalProps {
    /** Controls visibility of the modal. */
    open: boolean;
    /** Title text/node shown at the top of the modal. */
    title?: React.ReactNode;
    /** Body text/node. Ignored if `children` is provided. */
    message?: React.ReactNode;
    /** Custom body content. Overrides `message` when provided. */
    children?: React.ReactNode;
    /** Called when the user confirms. */
    onConfirm: () => void;
    /** Called when the user cancels (Cancel button, overlay click, or Escape). */
    onCancel: () => void;
    /** Label for the confirm button. Defaults to "Confirm". */
    confirmLabel?: string;
    /** Label for the cancel button. Defaults to "Cancel". */
    cancelLabel?: string;
    /** Shows a spinner and disables the confirm button. */
    isConfirming?: boolean;
    /** Label shown on the confirm button while `isConfirming` is true. */
    confirmingLabel?: string;
    /** Visual style of the confirm button. `danger` is red for destructive actions. */
    variant?: ConfirmModalVariant;
    /** Disables the confirm button regardless of `isConfirming`. */
    disableConfirm?: boolean;
    /** Hides the cancel button entirely. */
    hideCancelButton?: boolean;
    /** Hides the confirm button entirely. */
    hideConfirmButton?: boolean;
    /** Clicking the overlay backdrop calls `onCancel`. Defaults to true. */
    closeOnOverlayClick?: boolean;
    /** Pressing Escape calls `onCancel`. Defaults to true. */
    closeOnEscape?: boolean;
    /** Autofocuses the confirm button on open. Defaults to true. */
    autoFocusConfirm?: boolean;
    /** Optional icon/node rendered in a circular badge inline with the title. */
    icon?: React.ReactNode;
    /** Max width of the modal card, e.g. 480 or '480px'. Defaults to 480. */
    maxWidth?: number | string;
    /** Overrides the overlay's z-index. Defaults to 2000. */
    zIndex?: number;
    /** Overrides the id used to associate the title with the dialog for a11y. */
    titleId?: string;
    /** Additional className applied to the modal card. */
    className?: string;
    /** data-testid applied to the overlay, useful for tests. */
    testId?: string;
}

const ConfirmModal: FC<ConfirmModalProps> = ({
    open,
    title = 'Confirm',
    message,
    children,
    onConfirm,
    onCancel,
    confirmLabel = 'Confirm',
    cancelLabel = 'Cancel',
    isConfirming = false,
    confirmingLabel,
    variant = 'default',
    disableConfirm = false,
    hideCancelButton = false,
    hideConfirmButton = false,
    closeOnOverlayClick = true,
    closeOnEscape = true,
    autoFocusConfirm = true,
    icon,
    maxWidth = 480,
    zIndex = 2000,
    titleId = 'confirm-modal-title',
    className,
    testId
}) => {
    useEffect(() => {
        if (!open || !closeOnEscape) { return; }
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') { onCancel(); }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [open, closeOnEscape, onCancel]);

    if (!open) { return null; }

    const handleOverlayClick = () => {
        if (closeOnOverlayClick) { onCancel(); }
    };

    return (
        <ModalOverlay onClick={handleOverlayClick} $zIndex={zIndex} data-testid={testId}>
            <ModalCard
                role="alertdialog"
                aria-modal="true"
                aria-labelledby={title ? titleId : undefined}
                onClick={(e: React.MouseEvent) => e.stopPropagation()}
                $maxWidth={maxWidth}
                className={className}
            >
                {(icon || title) && (
                    <ModalHeader>
                        {icon && <ModalIcon>{icon}</ModalIcon>}
                        {title && <ModalTitle id={titleId}>{title}</ModalTitle>}
                    </ModalHeader>
                )}
                {children ? children : (message && <ModalMessage>{message}</ModalMessage>)}
                <ModalActions>
                    {!hideCancelButton && (
                        <CancelButton onClick={onCancel} type="button" disabled={isConfirming}>
                            {cancelLabel}
                        </CancelButton>
                    )}
                    {!hideConfirmButton && (
                        <ConfirmButton
                            onClick={onConfirm}
                            type="button"
                            autoFocus={autoFocusConfirm}
                            disabled={disableConfirm || isConfirming}
                            $variant={variant}
                        >
                            {isConfirming && <ButtonSpinner />}
                            {isConfirming ? (confirmingLabel || confirmLabel) : confirmLabel}
                        </ConfirmButton>
                    )}
                </ModalActions>
            </ModalCard>
        </ModalOverlay>
    );
};

export default ConfirmModal;

const overlayFadeIn = keyframes`
    from { opacity: 0; }
    to { opacity: 1; }
`;

const modalPopIn = keyframes`
    from { opacity: 0; transform: translateY(8px) scale(0.98); }
    to { opacity: 1; transform: translateY(0) scale(1); }
`;

const spin = keyframes`
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
`;

const ModalOverlay = styled.div<{ $zIndex: number }>`
    position: fixed;
    inset: 0;
    background: rgba(15, 23, 42, 0.45);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: ${({ $zIndex }) => $zIndex};
    animation: ${overlayFadeIn} 150ms ease-out;
`;

const ModalCard = styled.div<{ $maxWidth: number | string }>`
    width: 90%;
    max-width: ${({ $maxWidth }) => (typeof $maxWidth === 'number' ? `${$maxWidth}px` : $maxWidth)};
    background: #ffffff;
    border-radius: 12px;
    box-shadow: 0 12px 32px rgba(16, 24, 40, 0.2);
    padding: 24px;
    box-sizing: border-box;
    animation: ${modalPopIn} 180ms ease-out;
    font-family: 'Futura Md BT', helvetica, arial, sans-serif;
`;

const ModalHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 16px;
`;

const ModalIcon = styled.span`
    display: inline-flex;
    align-items: center;
    flex-shrink: 0;
`;

const ModalTitle = styled.h3`
    margin: 0;
    font-size: 1.1875rem;
    font-weight: 700;
    color: #0c5a45;
`;

const ModalMessage = styled.p`
    margin: 0;
    font-size: 1rem;
    line-height: 1.5;
    color: #4b5563;
`;

const ModalActions = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    margin-top: 22px;
`;

const CancelButton = styled.button`
    background: #ffffff;
    color: #374151;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    padding: 10px 18px;
    font-size: 0.9375rem;
    font-weight: 500;
    cursor: pointer;
    font-family: inherit;
    transition: background-color 140ms, opacity 140ms;

    &:hover:not(:disabled) { background: #f3f4f6; }
    &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

const confirmVariantColors: { [key in ConfirmModalVariant]: { bg: string; hover: string } } = {
    default: { bg: '#006A4D', hover: '#005440' },
    danger: { bg: '#c0392b', hover: '#a5311f' }
};

const ConfirmButton = styled.button<{ $variant: ConfirmModalVariant }>`
    display: inline-flex;
    align-items: center;
    gap: 8px;
    background-color: ${({ $variant }) => confirmVariantColors[$variant].bg};
    color: #ffffff;
    border: none;
    border-radius: 6px;
    padding: 10px 20px;
    font-size: 0.9375rem;
    font-weight: 500;
    cursor: pointer;
    font-family: inherit;
    transition: background-color 140ms, opacity 140ms;

    &:hover:not(:disabled) { background-color: ${({ $variant }) => confirmVariantColors[$variant].hover}; }
    &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

const ButtonSpinner = styled.span`
    display: inline-block;
    width: 14px;
    height: 14px;
    border: 2px solid rgba(255, 255, 255, 0.4);
    border-top-color: #fff;
    border-radius: 50%;
    animation: ${spin} 0.7s linear infinite;
`;
